import { APP_CONTAINER_ROUTES, APP_ROUTES } from '@/navigators/routes';
import { AppContainerStackParams, AppStackParams } from '@/navigators/types';
import {
  CompositeNavigationProp,
  useNavigation,
} from '@react-navigation/native';
import { InteractionManager } from 'react-native';
import { StackNavigationProp } from '@react-navigation/stack';
import { StorefrontBootIntent } from '@domain/microproducts/boot-params-types';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useEffect } from 'react';
import { useLayoutContext } from '@/contexts/layout-context';

// Both target param lists (the notification listener's pattern): the tab
// route on large/tablet, the container-stack route on small.
type NavigationType = CompositeNavigationProp<
  StackNavigationProp<AppStackParams, APP_ROUTES>,
  StackNavigationProp<AppContainerStackParams>
>;

/**
 * Performs the navigation half of the storefront boot intent: called inside
 * AppTabsContainerStack — i.e. INSIDE the selectedRestaurantUUID-keyed
 * subtree, beside the notification listener — so it always runs with live,
 * freshly mounted navigators.
 *
 * The pending intent lives above the key boundary (useStorefrontBootIntent)
 * and survives the restaurant-switch remount; this effect only fires once
 * the selected restaurant matches the intent (or the intent carries no
 * restaurant), which is exactly the post-remount instance for cross-
 * restaurant links. The pre-switch instance skips: anything it navigated
 * to would be torn down by the remount anyway.
 *
 * Target split mirrors the More-popup tap: the hidden
 * in-container tab on large/tablet, the pushed stack screen on the small
 * layout (which registers no tab). Tab routes are reachable from here by
 * plain navigate(name, params) — the notification listener's
 * navigate(APP_ROUTES.Orders, …) is the precedent.
 */
export const useStorefrontBootNavigation = (
  pendingNavigation: StorefrontBootIntent | null,
  clearPendingNavigation: () => void,
): void => {
  const { navigate } = useNavigation<NavigationType>();
  const { isSmallLayout } = useLayoutContext();
  const { selectedRestaurantUUID } = useAuthenticatedUserContext();

  useEffect(() => {
    if (!pendingNavigation) {
      return;
    }

    if (
      pendingNavigation.restaurantUUID !== undefined &&
      pendingNavigation.restaurantUUID !== selectedRestaurantUUID
    ) {
      // The restaurant switch is still in flight: this instance belongs to
      // the pre-switch subtree, which the key boundary is about to tear
      // down. The remounted instance navigates.
      return;
    }

    const { productId } = pendingNavigation;
    // Mirror the notification listener: let the (re)mounted tab navigator
    // finish initializing before navigating elsewhere. The pending intent
    // is cleared AFTER navigating, inside the callback: on web
    // runAfterInteractions maps to requestIdleCallback (no timeout), so
    // clearing up-front would consume the one-shot intent with the
    // navigate still unscheduled — a remount in that window would drop the
    // deep link. Clearing late means the worst case is a duplicate
    // idempotent navigate, never a lost link.
    InteractionManager.runAfterInteractions(() => {
      if (isSmallLayout) {
        navigate(APP_CONTAINER_ROUTES.StorefrontMicroProduct, { productId });
      } else {
        navigate(APP_ROUTES.StorefrontMicroProduct, { productId });
      }
      clearPendingNavigation();
    });
  }, [
    clearPendingNavigation,
    isSmallLayout,
    navigate,
    pendingNavigation,
    selectedRestaurantUUID,
  ]);
};
